import { Component, ErrorInfo } from 'react';
import type React from 'react';
import SearchAnalyticsContext from '~/context/srp/SearchAnalyticsContext';
import generateErrorLogger from '~/utilities/srp/errorHandlers';
import ErrorBoundaryFallbackComponent, {
  OwnProps as ErrorBoundaryFallbackComponentProps
} from './ErrorBoundaryFallbackComponent';

type OwnProps = {
  children: React.ReactNode;
  FallbackComponent?: React.ComponentType<ErrorBoundaryFallbackComponentProps>;
  onError?: (_error: Error, _errorInfo: ErrorInfo) => void;
};

type OwnState = {
  error?: Error;
  info?: ErrorInfo;
};

const logError = generateErrorLogger('Global Error Boundary');

export default class ErrorBoundary extends Component<OwnProps, OwnState> {
  static contextType = SearchAnalyticsContext;

  constructor(props: OwnProps) {
    super(props);

    this.state = {
      error: undefined,
      info: undefined
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { trackEvent } = this.context;
    const { onError = logError } = this.props;
    const payload = {
      error: error.message,
      stack: error.stack
    };
    trackEvent('srpFatalError', payload);

    try {
      onError(error, info);
    } catch (ignoreError) {
      logError(error);
    }

    this.setState({ error, info });
  }

  declare context: React.ContextType<typeof SearchAnalyticsContext>;

  render() {
    const { children, FallbackComponent = ErrorBoundaryFallbackComponent } =
      this.props;
    const { error, info } = this.state;

    return error ? (
      <FallbackComponent
        componentStack={info?.componentStack ?? ''}
        error={error}
      />
    ) : (
      children
    );
  }
}

export const withErrorBoundary =
  <P,>(
    ChildComponent: React.ComponentType<P>,
    FallbackComponent: OwnProps['FallbackComponent'] = (
      _: ErrorBoundaryFallbackComponentProps
    ) => null,
    onError: OwnProps['onError'] = generateErrorLogger(
      ChildComponent.displayName ?? 'Component Error Boundary'
    )
  ) =>
  props =>
    (
      <ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
        <ChildComponent {...props} />
      </ErrorBoundary>
    );
